// src/Services/publicRoomService.js
import {
    collection,
    query,
    where,
    orderBy,
    limit,
    getDocs,
    Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { getUserCountry, getCountryNameFromCode } from './countryUtils';

class PublicRoomsService {
    constructor() {
        this.ROOMS_PER_SHUFFLE = 15;
    }

    // Convert Firestore document to PublicRoom
    convertDocToRoom(doc) {
        const data = doc.data();
        return {
            roomId: data.roomId,
            roomName: data.roomName,
            hostName: data.players?.[0]?.name || 'Unknown',
            playerCount: data.players?.length || 0,
            maxPlayers: data.maxPlayers || 3,
            gameStatus: data.gameStatus,
            country: data.country || 'Unknown',
            categories: data.categories || [],
            createdAt: data.createdAt?.toDate?.() || new Date(),
        };
    }

    // Get user's country for filtering
    async getUserCountry() {
        return getUserCountry();
    }

    // Convert country code to country name
    getCountryNameFromCode(countryCode) {
        return getCountryNameFromCode(countryCode);
    }

    // Filter rooms based on criteria
    filterRooms(rooms, filters) {
        let filteredRooms = [...rooms];

        // Filter by player count
        if (filters.playerCountFilter && filters.playerCountFilter !== 'all') {
            if (filters.playerCountFilter === 'less-than-3') {
                filteredRooms = filteredRooms.filter((room) => room.playerCount < 3);
            } else if (filters.playerCountFilter === 'more-than-3') {
                filteredRooms = filteredRooms.filter((room) => room.playerCount >= 3);
            }
        }

        // Filter by country
        if (filters.country && filters.country !== 'all') {
            filteredRooms = filteredRooms.filter((room) => room.country === filters.country);
        }

        // Filter by search query (room name)
        if (filters.searchQuery && filters.searchQuery.trim()) {
            const searchTerm = filters.searchQuery.toLowerCase().trim();
            filteredRooms = filteredRooms.filter((room) =>
                room.roomName.toLowerCase().includes(searchTerm)
            );
        }

        return filteredRooms;
    }

    // Shuffle array and take first N items
    shuffleAndLimit(array, limit) {
        const shuffled = [...array];

        // Fisher-Yates shuffle algorithm
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }

        return shuffled.slice(0, limit);
    }

    // Priority sort for rooms (better rooms first)
    prioritizeRooms(rooms) {
        const now = new Date();
        const fifteenMinutesAgo = new Date(now.getTime() - 15 * 60 * 1000);
        const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

        return rooms.sort((a, b) => {
            // Priority 1: Fresh rooms (under 15 minutes) get top priority
            const aIsFresh = a.createdAt > fifteenMinutesAgo;
            const bIsFresh = b.createdAt > fifteenMinutesAgo;

            if (aIsFresh && !bIsFresh) return -1;
            if (!aIsFresh && bIsFresh) return 1;

            // Priority 2: Within fresh rooms, prefer sweet spot player count (2-3 players)
            if (aIsFresh && bIsFresh) {
                const aInSweetSpot = a.playerCount >= 2 && a.playerCount <= 3;
                const bInSweetSpot = b.playerCount >= 2 && b.playerCount <= 3;

                if (aInSweetSpot && !bInSweetSpot) return -1;
                if (!aInSweetSpot && bInSweetSpot) return 1;

                // Within sweet spot, prefer more players
                if (aInSweetSpot && bInSweetSpot) {
                    return b.playerCount - a.playerCount;
                }

                // If neither in sweet spot, prefer newer
                return b.createdAt.getTime() - a.createdAt.getTime();
            }

            // Priority 3: Medium age rooms (15 minutes to 1 hour)
            const aIsMedium = a.createdAt > oneHourAgo && a.createdAt <= fifteenMinutesAgo;
            const bIsMedium = b.createdAt > oneHourAgo && b.createdAt <= fifteenMinutesAgo;

            if (aIsMedium && !bIsMedium) return -1;
            if (!aIsMedium && bIsMedium) return 1;

            // Within medium age, prefer rooms with players
            if (aIsMedium && bIsMedium) {
                if (a.playerCount > 1 && b.playerCount === 1) return -1;
                if (a.playerCount === 1 && b.playerCount > 1) return 1;

                // If both have similar player counts, prefer newer
                return b.createdAt.getTime() - a.createdAt.getTime();
            }

            // Priority 4: Old rooms get pushed to bottom but still shown
            const aIsOld = a.createdAt <= oneHourAgo;
            const bIsOld = b.createdAt <= oneHourAgo;

            if (aIsOld && !bIsOld) return 1;
            if (!aIsOld && bIsOld) return -1;

            // Both are old - sort by player count then by creation time
            if (aIsOld && bIsOld) {
                if (a.playerCount !== b.playerCount) {
                    return b.playerCount - a.playerCount;
                }
                return b.createdAt.getTime() - a.createdAt.getTime();
            }

            // Final fallback - newer first
            return b.createdAt.getTime() - a.createdAt.getTime();
        });
    }

    // Fetch all available public rooms from Firebase
    async fetchAllPublicRooms() {
        try {
            const roomsCollection = collection(db, 'rooms');

            const q = query(
                roomsCollection,
                where('isPublic', '==', true),
                where('isActive', '==', true),
                where('gameStatus', '==', 'waiting'),
                orderBy('createdAt', 'desc')
            );

            const querySnapshot = await getDocs(q);
            const rooms = [];
            const now = new Date();
            const fifteenMinutesAgo = new Date(now.getTime() - 15 * 60 * 1000);

            querySnapshot.forEach((doc) => {
                try {
                    const roomData = doc.data();

                    // Skip rooms that are explicitly hidden
                    if (roomData.publicRoomHiddenAt) {
                        return;
                    }

                    // Skip rooms older than 15 minutes
                    const roomCreatedAt = roomData.createdAt?.toDate?.() || new Date(roomData.createdAt);
                    if (roomCreatedAt < fifteenMinutesAgo) {
                        return;
                    }

                    const room = this.convertDocToRoom(doc);

                    // Only include rooms with available slots
                    if (room.playerCount < room.maxPlayers) {
                        rooms.push(room);
                    }
                } catch (error) {
                    console.warn('Error converting room document:', error);
                }
            });

            console.log(`Fetched ${rooms.length} public rooms`);
            return rooms;
        } catch (error) {
            console.error('Error fetching public rooms:', error);
            throw error;
        }
    }

    // Get shuffled public rooms with filters
    async getPublicRooms(filters = {}) {
        try {
            console.log('Fetching public rooms with filters:', filters);

            const allRooms = await this.fetchAllPublicRooms();
            const filteredRooms = this.filterRooms(allRooms, filters);
            const prioritizedRooms = this.prioritizeRooms(filteredRooms);
            const shuffledRooms = this.shuffleAndLimit(prioritizedRooms, this.ROOMS_PER_SHUFFLE);

            return {
                success: true,
                rooms: shuffledRooms,
                message: `Found ${shuffledRooms.length} rooms`,
                totalAvailable: filteredRooms.length,
            };
        } catch (error) {
            console.error('Error getting public rooms:', error);

            return {
                success: false,
                rooms: [],
                message: error.message || 'Failed to load public rooms',
                totalAvailable: 0,
            };
        }
    }

    // Get available countries from current public rooms
    async getAvailableCountries() {
        try {
            const allRooms = await this.fetchAllPublicRooms();
            const countries = [...new Set(allRooms.map((room) => room.country))];

            // Sort countries, putting user's country first
            const userCountry = await this.getUserCountry();
            const sortedCountries = countries.sort((a, b) => {
                if (a === userCountry) return -1;
                if (b === userCountry) return 1;
                return a.localeCompare(b);
            });

            return sortedCountries;
        } catch (error) {
            console.error('Error getting available countries:', error);
            return ['Nigeria'];
        }
    }

    // Refresh/reshuffle rooms
    async reshuffleRooms(filters = {}) {
        console.log('Reshuffling public rooms...');
        return this.getPublicRooms(filters);
    }
}

// Export singleton instance
export const publicRoomsService = new PublicRoomsService();
export default publicRoomsService;